import { getGradeReportbyCourse } from "./courses";

/**
 * Resolves the course final grade following Moodle's institutional rule:
 * 1. Checks all gradable activity items (excluding hidden and folder items).
 * 2. If all activities are graded, extracts the official course total grade
 *    calculated by Moodle (`itemtype === "course"`), preserving weighted
 *    categories and native gradebook scales (e.g. 1-7 or 0-100%).
 * 3. If activities are only partially graded, marks the cumulative grade
 *    as `isCompleted: false` so the UI can flag it as "En progreso".
 * 4. If no activities are graded or none exist, returns `null`.
 *
 * @param {Array} gradeitems - usergrades[0].gradeitems from gradereport_user_get_grade_items
 * @returns {{ raw: number | null, formatted: string, isCompleted: boolean } | null}
 */
export function computeFinalGrade(gradeitems) {
  if (!Array.isArray(gradeitems) || gradeitems.length === 0) return null;

  const { activityItems, gradedItems } = splitActivityItems(gradeitems);
  if (activityItems.length === 0 || gradedItems.length === 0) return null;

  const isCompleted = gradedItems.length === activityItems.length;
  const courseTotalItem = gradeitems.find(
    (item) => item.itemtype === "course" && !item.gradeishidden
  );

  let raw = null;
  let formatted = "-";

  if (courseTotalItem) {
    if (courseTotalItem.graderaw !== null && courseTotalItem.graderaw !== undefined) {
      raw = Number(courseTotalItem.graderaw);
    }
    formatted = formatCourseGrade(courseTotalItem, raw);
  }

  // Fallback to average of graded items if course total is missing or empty
  if (formatted === "-" || formatted === null) {
    const sum = gradedItems.reduce((acc, item) => acc + Number(item.graderaw), 0);
    const average = sum / gradedItems.length;
    raw = average;
    formatted = formatGrade(average);
  }

  return { raw, formatted, isCompleted };
}

/**
 * Course progress as % of gradable activities that already have a grade.
 *
 * @param {Array} gradeitems - usergrades[0].gradeitems from gradereport_user_get_grade_items
 * @returns {number|null} 0-100, or null when the course has no gradable activities
 */
export function computeCourseProgress(gradeitems) {
  const { activityItems, gradedItems } = splitActivityItems(gradeitems);
  if (activityItems.length === 0) return null;

  return (gradedItems.length * 100) / activityItems.length;
}

export function splitActivityItems(gradeitems) {
  if (!Array.isArray(gradeitems)) return { activityItems: [], gradedItems: [] };

  const activityItems = gradeitems.filter(
    (d) =>
      d.itemtype !== "course" &&
      d.itemtype !== "category" &&
      d.itemmodule !== "folder" &&
      d.itemname !== null &&
      d.itemname !== ""
  );
  const gradedItems = activityItems.filter(
    (item) =>
      !item.gradeishidden &&
      item.graderaw !== null &&
      item.graderaw !== undefined
  );

  return { activityItems, gradedItems };
}

export function formatCourseGrade(courseItem, fallbackRaw) {
  if (courseItem?.gradeformatted && courseItem.gradeformatted.trim() !== "-") {
    const trimmed = courseItem.gradeformatted.trim();
    // If Moodle displays "Real (Percentage)" e.g. "6,50 (92.86 %)", keep the primary grade
    if (trimmed.includes("(")) {
      return trimmed.split("(")[0].trim();
    }
    return trimmed;
  }

  if (fallbackRaw !== null && fallbackRaw !== undefined && !isNaN(fallbackRaw)) {
    return formatGrade(fallbackRaw);
  }

  return "-";
}

export function formatGrade(value) {
  const rounded = Number(`${Math.round(Number(`${value}e1`))}e-1`);
  return rounded.toFixed(1).replace(".", ",");
}

/**
 * Composed entry point: fetches per-user grade items for a course and
 * resolves both the final grade and the progress from the response.
 *
 * @param {{ token: string, id: number|string, courseId: number|string }} args
 * @returns {Promise<{ finalGrade: { raw: number|null, formatted: string, isCompleted: boolean } | null, progress: number | null }>}
 */
export async function resolveCourseGradeAndProgress({ token, id, courseId }) {
  try {
    const data = await getGradeReportbyCourse({ token, id, course_id: courseId });
    const gradeitems = data?.usergrades?.[0]?.gradeitems;
    return {
      finalGrade: computeFinalGrade(gradeitems),
      progress: computeCourseProgress(gradeitems),
    };
  } catch {
    return { finalGrade: null, progress: null };
  }
}
